import { Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { SignUpModal } from "@/components/auth/SignUpModal";
import { WizardProtectedRoute } from "@/components/auth/WizardProtectedRoute";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { UserAvatarDropdown } from "@/components/layout/user-avatar-dropdown";
import { PaywallDialog } from "@/components/PaywallDialog";
import { Step0Identification } from "@/components/steps/step-0-identification";
import { Step2ServiceMapping } from "@/components/steps/step-2-service-mapping";
import type { SelectedService } from "@/types/service";

import { Step3NISClassification } from "@/components/steps/step-3-nis-classification";
import { Step3SizeEvaluation } from "@/components/steps/step-3-size-evaluation";
import {
  Step5ReportGeneration,
  type ReportData,
} from "@/components/steps/step-5-report-generation";
import { LoadingWizardSession } from "@/components/ui/loading-wizard-session";
import { SidebarProvider } from "@/components/ui/sidebar";
import { MAIN_WIZARD_STEPS, POSTHOG_EVENTS } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { useWizardSession } from "@/hooks/useWizardSession";
import type { EntityData } from "@/types/entity";
import { createDefaultEntityData } from "@/utils/entityDefaults";
import { loadSessionData } from "@/utils/database";
import { saveUnauthenticatedEntityData } from "@/utils/localStorageEntityData";
import { haveServicesChanged } from "@/utils/serviceUtils";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";

export default function NIS2Wizard() {
  const posthog = usePostHog();

  const [currentStep, setCurrentStep] = useState(0);
  const [entityData, setEntityData] = useState<EntityData | undefined>(
    undefined
  );
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>(
    []
  );

  const [sectorClassification, setSectorClassification] = useState<any>(null);

  const [isLoadingSessionData, setIsLoadingSessionData] = useState(false);

  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Sign-up modal state
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  // Paywall state for credit deduction
  const [showPaywall, setShowPaywall] = useState(false);

  // Session management
  const {
    sessionId,
    isLoading: sessionLoading,
    updateProgress,
    saveEntity,
    completeSession,
    deleteCurrentEntityData,
  } = useWizardSession();

  useEffect(() => {
    const effectId = Math.random().toString(36).substr(2, 9);

    // Only restore state when we have a valid sessionId and aren't loading
    if (!sessionId || sessionLoading) {
      console.log("🔄 WIZARD[STATE_RESTORE_SKIP]:", {
        sessionId,
        sessionLoading,
        effectId,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    const restoreState = async () => {
      console.log("🔄 WIZARD[STATE_RESTORE_START]:", {
        sessionId,
        effectId,
        timestamp: new Date().toISOString(),
      });

      try {
        setIsLoadingSessionData(true);
        // Load fresh data for the current session
        const data = await loadSessionData(sessionId);

        if (data?.entityData) {
          console.log("🔄 WIZARD[STATE_RESTORE_DATA_FOUND]:", {
            sessionId,
            dataKeys: Object.keys(data),
            currentStep: data.currentStep,
            effectId,
            timestamp: new Date().toISOString(),
          });

          // Restore all state from loaded data atomically
          console.log("🚀 ~ restoreState ~ data.entityData:", data.entityData);
          const restoredState = {
            entityData: data.entityData,
            selectedServices: data.entityData?.selected_services || [],
            entitySize: data.entityData?.entity_size || "MARE",
            financialData: {
              nrSalariati: data.entityData?.nr_salariati || 0,
              cifraAfaceri: data.entityData?.cifra_afaceri || 0,
              active: data.entityData?.active || 0,
            },
            currentStep: data.currentStep || 0,
          };

          setEntityData(restoredState.entityData);
          setSelectedServices(restoredState.selectedServices || []);

          // Restore evaluations
          if (data.evaluations) {
            data.evaluations.forEach((evaluation: any) => {
              if (evaluation.evaluation_type === "sector_classification") {
                setSectorClassification(evaluation.criteria);
              }
            });
          } else {
            setSectorClassification(null);
          }

          // Set current step last to ensure all state is ready
          setCurrentStep(restoredState.currentStep);

          console.log("🔄 WIZARD[STATE_RESTORE_COMPLETE]:", {
            sessionId,
            restoredStep: restoredState.currentStep,
            effectId,
            timestamp: new Date().toISOString(),
          });
        } else {
          // Reset to defaults if no data
          console.log("🔄 WIZARD[STATE_RESTORE_DEFAULTS]:", {
            sessionId,
            effectId,
            timestamp: new Date().toISOString(),
          });

          setEntityData(createDefaultEntityData(sessionId));
          setSelectedServices([]);
          setSectorClassification(null);
          setCurrentStep(0);
        }
      } catch (error) {
        console.error("Error restoring session data:", error);
        toast({
          title: "Eroare",
          description: "Nu s-a putut încărca sesiunea",
          variant: "destructive",
        });
        setIsLoadingSessionData(false);
      } finally {
        setIsLoadingSessionData(false);
      }
    };

    restoreState();
  }, [sessionId, sessionLoading]); // Only depend on sessionId and loading state

  const handleNext = async () => {
    const newStep = Math.min(currentStep + 1, MAIN_WIZARD_STEPS.length - 1);
    setCurrentStep(newStep);
    await updateProgress(newStep, 0);
  };

  const handleBack = async () => {
    // Prevent navigation on mobile
    if (isMobile) return;

    const newStep = Math.max(currentStep - 1, 0);
    setCurrentStep(newStep);
    await updateProgress(newStep, 0);
  };

  const handleStepClick = async (stepIndex: number) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
      await updateProgress(stepIndex, 0);

      if (stepIndex === 0) {
        await deleteCurrentEntityData();
        setEntityData(createDefaultEntityData(sessionId));
        setSelectedServices([]);
        setSectorClassification(null);
        setCurrentStep(0);
      }
    }
  };

  const renderCurrentStep = () => {
    const stepContent = (() => {
      switch (currentStep) {
        case 0:
          return (
            <Step0Identification
              key={sessionId}
              currentStep={0}
              steps={MAIN_WIZARD_STEPS}
              entityData={entityData}
              onNext={async (cui, anafData) => {
                console.log("🚀 ~ NIS2Wizard ~ debug ~ anafData:", anafData);
                let newEntityData: EntityData;

                if (anafData) {
                  // Use ANAF data if available
                  newEntityData = anafData;
                  setEntityData(anafData);
                } else {
                  // Fallback to minimal entity data with session ID
                  newEntityData = createDefaultEntityData(sessionId);
                  setEntityData(newEntityData);
                }

                // Save to database if authenticated, otherwise save to localStorage
                if (user) {
                  await saveEntity(newEntityData);
                } else {
                  saveUnauthenticatedEntityData(
                    newEntityData,
                    selectedServices,
                    1
                  );
                }

                posthog.capture(POSTHOG_EVENTS.STEP_1_CUI);

                handleNext();
              }}
              onStepClick={handleStepClick}
              userAvatar={<UserAvatarDropdown />}
            />
          );

        case 1:
          return (
            <Step3SizeEvaluation
              entityData={entityData}
              currentStep={1}
              steps={MAIN_WIZARD_STEPS}
              onStepClick={handleStepClick}
              isLoading={false}
              sessionId={sessionId}
              onNext={async (size, financial) => {
                const updatedEntity: EntityData = {
                  ...entityData,
                  nr_salariati: financial.nrSalariati,
                  cifra_afaceri: financial.cifraAfaceri,
                  active: financial.active,
                  dimensiune: size,
                };
                setEntityData(updatedEntity);

                // Save to database if authenticated, otherwise save to localStorage
                if (user) {
                  await saveEntity(updatedEntity);
                  handleNext();
                } else {
                  // Unauthenticated user: Save to localStorage
                  saveUnauthenticatedEntityData(
                    updatedEntity,
                    selectedServices,
                    2
                  );
                  handleNext();
                }
              }}
              onBack={handleBack}
              userAvatar={<UserAvatarDropdown />}
            />
          );

        case 2:
          return (
            <Step2ServiceMapping
              entityData={entityData}
              currentStep={2}
              steps={MAIN_WIZARD_STEPS}
              onStepClick={handleStepClick}
              onNext={async (
                services,
                sectorData,
                caenSecundar,
                relatieStatRoman,
                situatieServiciiUE
              ) => {
                const servicesChanged = haveServicesChanged(
                  entityData?.selected_services,
                  services
                );

                setSelectedServices(services);
                setSectorClassification(sectorData);

                const updatedEntity: EntityData = {
                  ...entityData,
                  selected_services: services,
                  caen_secundar: caenSecundar,
                  relatie_stat_roman: relatieStatRoman,
                  situatie_servicii_ue: situatieServiciiUE,
                  ...(servicesChanged && {
                    nis_classification: null,
                    impact_analysis: null,
                  }),
                };
                setEntityData(updatedEntity);

                await saveEntity(updatedEntity);

                posthog.capture(POSTHOG_EVENTS.STEP_3_SELECTED_SERVICES);
                handleNext();
              }}
              onBack={handleBack}
              userAvatar={<UserAvatarDropdown />}
            />
          );

        case 3:
          return (
            <Step3NISClassification
              entityData={entityData}
              selectedServices={selectedServices}
              sectorClassification={sectorClassification}
              currentStep={3}
              steps={MAIN_WIZARD_STEPS}
              onStepClick={handleStepClick}
              onNext={async (classification) => {
                // Update entity data state with the classification so Step 5 receives it
                const updatedEntity: EntityData = {
                  ...entityData,
                  nis_classification: classification,
                };
                setEntityData(updatedEntity);
                await saveEntity(updatedEntity);

                posthog.capture(POSTHOG_EVENTS.STEP_4_NIS_CLASSIFICATION);
                setCurrentStep(4);
                await updateProgress(4, 0);
              }}
              onBack={handleBack}
              userAvatar={<UserAvatarDropdown />}
            />
          );

        case 4:
          return (
            <Step5ReportGeneration
              entityData={entityData}
              currentStep={4}
              steps={MAIN_WIZARD_STEPS}
              onStepClick={handleStepClick}
              onNext={async (report: ReportData) => {
                const updatedEntity: EntityData = {
                  ...entityData,
                  contact_data: report.contactData,
                  documente_anexate: report.documenteAnexate,
                  email: report.companyEmail,
                  telefon: report.companyTelefon,
                };
                setEntityData(updatedEntity);
                await saveEntity(updatedEntity);

                posthog.capture(POSTHOG_EVENTS.STEP_5_REPORT_GENERATION);

                await completeSession();
              }}
              onBack={handleBack}
              userAvatar={<UserAvatarDropdown />}
            />
          );

        default:
          return (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center space-y-2">
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="text-muted-foreground">Se încarcă...</p>
              </div>
            </div>
          );
      }
    })();

    return stepContent;
  };

  const handleSignUpSuccess = () => {
    setShowSignUpModal(false);
    // After successful sign-up, proceed to the next step
    handleNext();
  };

  const handleSignUpModalClose = () => {
    setShowSignUpModal(false);
  };

  if (sessionLoading || isLoadingSessionData) {
    return <LoadingWizardSession />;
  }

  return (
    <WizardProtectedRoute currentStep={currentStep}>
      <SidebarProvider>
        <div className="min-h-screen bg-background flex w-full">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <main className="flex-1">{renderCurrentStep()}</main>
          </div>
        </div>

        {/* Sign-up Modal */}
        <SignUpModal
          isOpen={showSignUpModal}
          onClose={handleSignUpModalClose}
          onSignUpSuccess={handleSignUpSuccess}
        />

        {/* Paywall Dialog for insufficient credits */}
        <PaywallDialog
          open={showPaywall}
          onOpenChange={setShowPaywall}
          actionType="evaluare_risc_enire"
        />
      </SidebarProvider>
    </WizardProtectedRoute>
  );
}
